import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLangPrefix } from '../hooks/useLocalePath';
import SEO from '../components/SEO';
import '../styles/Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const langPrefix = useLangPrefix();
  const [email, setEmail] = useState('');
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ferme le menu mobile quand on clique sur un lien
  const closeMenu = () => setMobileMenuOpen(false);

  const features = t('landing.features.items', { returnObjects: true });
  const featureIcons = ['target', 'layers', 'users', 'bar-chart', 'shield', 'zap'];

  const stats = t('landing.stats.items', { returnObjects: true });

  const useCases = t('landing.useCases.items', { returnObjects: true });
  const useCaseIconKeys = ['briefcase', 'code', 'building'];

  const testimonials = t('landing.testimonials.items', { returnObjects: true });
  const testimonialAvatars = ['ER', 'JM', 'SL', 'NG'];
  const testimonialRatings = [5, 5, 5, 5];

  const plans = t('landing.pricing.plans', { returnObjects: true });
  const planLinks = [langPrefix + '/register', langPrefix + '/register', langPrefix + '/contact?subject=enterprise'];
  const planPopular = [false, true, false];

  const faqs = t('landing.faq.items', { returnObjects: true });

  const handleCTA = () => {
    if (email) {
      navigate(langPrefix + `/register?email=${encodeURIComponent(email)}`);
    } else {
      navigate(langPrefix + '/register');
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const featureIconsMap = {
    target: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    layers: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
    users: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    'bar-chart': <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
    shield: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    zap: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  };

  const stepIcons = {
    folder: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    pencil: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    'check-circle': <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  };

  const useCaseIconsMap = {
    briefcase: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    code: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
    building: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><line x1="8" y1="6" x2="8" y2="6"/><line x1="12" y1="6" x2="12" y2="6"/><line x1="16" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/></svg>,
  };

  const howItWorksSteps = t('landing.howItWorks.steps', { returnObjects: true });
  const stepIconKeys = ['folder', 'pencil', 'check-circle'];

  return (
    <div className="landing">
      <SEO
        title={t('landing.seo.title')}
        description={t('landing.seo.description')}
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'ReviewSight',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          url: 'https://reviewsight.io',
          description: 'Plateforme de revue visuelle et QA collaborative pour les équipes web',
          offers: [
            { '@type': 'Offer', name: 'Starter', price: '9.99', priceCurrency: 'EUR', url: 'https://reviewsight.io/register' },
            { '@type': 'Offer', name: 'Pro', price: '29', priceCurrency: 'EUR', url: 'https://reviewsight.io/register' },
            { '@type': 'Offer', name: 'Enterprise', price: '69', priceCurrency: 'EUR', url: 'https://reviewsight.io/contact?subject=enterprise' }
          ],
          creator: { '@type': 'Organization', name: 'Viking Production', url: 'https://reviewsight.io/a-propos' },
          aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '2400', bestRating: '5' }
        }}
      />

      {/* ===== Navigation ===== */}
      <nav className={`landing-nav ${isNavScrolled ? 'nav-scrolled' : ''}`} role="navigation" aria-label={t('landing.nav.ariaLabel')}>
        <div className="landing-nav-inner">
          <div className="landing-logo" aria-label={t('landing.nav.logoAriaLabel')}>
            Review<span>Sight</span>
          </div>

          <div className={`landing-nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#features" onClick={closeMenu}>{t('landing.nav.features')}</a>
            <a href="#use-cases" onClick={closeMenu}>{t('landing.nav.solutions')}</a>
            <a href="#pricing" onClick={closeMenu}>{t('landing.nav.pricing')}</a>
            <a href="#testimonials" onClick={closeMenu}>{t('landing.nav.testimonials')}</a>
            <a href="#faq" onClick={closeMenu}>{t('landing.nav.faq')}</a>
          </div>

          <div className="landing-nav-actions">
            <Link to={langPrefix + '/login'} className="btn-nav-link">{t('landing.nav.login')}</Link>
            <Link to={langPrefix + '/register'} className="btn-nav-cta">{t('landing.nav.getStarted')}</Link>
          </div>

          <button
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'is-open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={t('landing.nav.mobileMenuAriaLabel')}
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* ===== Hero ===== */}
      <section className="landing-hero" aria-labelledby="hero-headline">
        <div className="hero-bg-effects" aria-hidden="true">
          <div className="hero-gradient-orb hero-orb-1"></div>
          <div className="hero-gradient-orb hero-orb-2"></div>
          <div className="hero-gradient-orb hero-orb-3"></div>
          <div className="hero-grid-pattern"></div>
        </div>

        <div className="hero-content">
          <div className="hero-badge" role="note">
            <span className="badge-dot" aria-hidden="true"></span>
            {t('landing.hero.badge')}
          </div>

          <h1 id="hero-headline">
            {t('landing.hero.headline')}<br />
            <span className="hero-accent">{t('landing.hero.headlineAccent')}</span>
          </h1>

          <p className="hero-subtitle">
            {t('landing.hero.subtitle')}
          </p>

          <div className="hero-cta">
            <div className="hero-email-group">
              <input
                type="email"
                placeholder={t('landing.hero.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCTA()}
                aria-label={t('landing.hero.emailAriaLabel')}
              />
              <button className="btn-hero-cta" onClick={handleCTA}>
                {t('landing.hero.cta')}
              </button>
            </div>
            <div className="hero-trust-note" aria-label={t('landing.hero.trustAriaLabel')}>
              <span>{t('landing.hero.trust1')}</span>
              <span className="dot-sep" aria-hidden="true"></span>
              <span>{t('landing.hero.trust2')}</span>
              <span className="dot-sep" aria-hidden="true"></span>
              <span>{t('landing.hero.trust3')}</span>
            </div>
          </div>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="hero-browser-mockup">
            <div className="browser-bar">
              <div className="browser-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
                            <div className="browser-url">{t('landing.hero.mockupUrl')}</div>
              <div className="browser-actions">
                <span className="browser-btn-icon">↺</span>
              </div>
            </div>
            <div className="browser-content">
              <div className="mockup-site">
                <div className="mockup-site-nav"></div>
                <div className="mockup-site-hero"></div>
                <div className="mockup-site-cards">
                  <div className="mockup-site-card"></div>
                  <div className="mockup-site-card"></div>
                  <div className="mockup-site-card"></div>
                </div>
              </div>

              <div className="mockup-annotation mockup-annotation-1">
                <div className="mockup-annotation-dot priority-high"></div>
                <div className="mockup-annotation-label">
                  <span className="annotation-priority high">{t('landing.hero.mockupAnnotations.high')}</span>
                  {t('landing.hero.mockupAnnotations.alignmentLabel')}
                </div>
              </div>
              <div className="mockup-annotation mockup-annotation-2">
                <div className="mockup-annotation-dot priority-critical"></div>
                <div className="mockup-annotation-label">
                  <span className="annotation-priority critical">{t('landing.hero.mockupAnnotations.critical')}</span>
                  {t('landing.hero.mockupAnnotations.ctaColorLabel')}
                </div>
              </div>
              <div className="mockup-annotation mockup-annotation-3">
                <div className="mockup-annotation-dot priority-medium"></div>
                <div className="mockup-annotation-label">
                  <span className="annotation-priority medium">{t('landing.hero.mockupAnnotations.medium')}</span>
                  {t('landing.hero.mockupAnnotations.spacingLabel')}
                </div>
              </div>

              <div className="mockup-panel">
                <div className="mockup-panel-header">
                  <span>{t('landing.hero.mockupAnnotations.panelAnnotationsCount')}</span>
                  <span className="panel-status">{t('landing.hero.mockupAnnotations.panelStatus')}</span>
                </div>
                <div className="mockup-panel-item">
                  <span className="panel-dot critical"></span>
                  <span>{t('landing.hero.mockupAnnotations.panelCtaColor')}</span>
                  <span className="panel-assignee">CD</span>
                </div>
                <div className="mockup-panel-item">
                  <span className="panel-dot high"></span>
                  <span>{t('landing.hero.mockupAnnotations.panelLogo')}</span>
                  <span className="panel-assignee">AM</span>
                </div>
                <div className="mockup-panel-item resolved">
                  <span className="panel-dot medium"></span>
                  <span>{t('landing.hero.mockupAnnotations.panelSpacing')}</span>
                  <span className="panel-check">✓</span>
                </div>
              </div>

                                          {/* Zone drawing animation */}
                <div className="mockup-zone-draw">
                  <div className="mockup-zone-rect"></div>
                  <div className="mockup-zone-handles">
                    <span className="mockup-zone-handle tl"></span>
                    <span className="mockup-zone-handle tr"></span>
                    <span className="mockup-zone-handle bl"></span>
                    <span className="mockup-zone-handle br"></span>
                  </div>
                </div>

                {/* Create issue popup (report a bug) */}
                <div className="mockup-create-popup">
                  <div className="mockup-create-popup-header">
                    <span>{t('landing.hero.mockupCreatePopup.title')}</span>
                    <span className="mockup-popup-close">&times;</span>
                  </div>
                  <div className="mockup-create-popup-field">
                    <span className="mockup-create-popup-label">{t('landing.hero.mockupCreatePopup.priorityLabel')}</span>
                    <span className="mockup-create-popup-select critical">{t('landing.hero.mockupAnnotations.critical')}</span>
                  </div>
                  <div className="mockup-create-popup-field">
                    <span className="mockup-create-popup-label">{t('landing.hero.mockupCreatePopup.descLabel')}</span>
                    <div className="mockup-create-popup-textarea">{t('landing.hero.mockupCreatePopup.descValue')}</div>
                  </div>
                  <div className="mockup-create-popup-actions">
                    <span className="mockup-popup-btn">{t('landing.hero.mockupCreatePopup.submit')}</span>
                  </div>
                </div>

                {/* Sidebar detail expansion */}
                <div className="mockup-sidebar-detail">
                  <div className="mockup-sidebar-detail-header">
                    <span className="mockup-popup-priority critical">{t('landing.hero.mockupAnnotations.critical')}</span>
                    <span className="mockup-sidebar-detail-id">#12</span>
                  </div>
                  <div className="mockup-sidebar-detail-title">{t('landing.hero.mockupPopup.title')}</div>
                  <div className="mockup-sidebar-detail-meta">
                    <span className="mockup-popup-author">CD</span>
                    <span>{t('landing.hero.mockupPopup.timeAgo')}</span>
                  </div>
                  <div className="mockup-sidebar-detail-desc">{t('landing.hero.mockupPopup.description')}</div>
                  <div className="mockup-sidebar-detail-comments">
                    <span className="mockup-popup-btn outline">{t('landing.hero.mockupPopup.comment')}</span>
                  </div>
                </div>
                    </div>
          </div>
        </div>

                                                                            {/* ===== Video Demo (EN only) ===== */}
          {langPrefix === '/en' && (
            <div className="hero-video-demo">
              <button className="btn-video-demo" onClick={() => setVideoOpen(true)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                Watch the 5-min demo
              </button>
            </div>
          )}

          {/* Video Modal */}
          {videoOpen && (
            <div className="video-modal-overlay" onClick={() => setVideoOpen(false)}>
              <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="video-modal-close" onClick={() => setVideoOpen(false)}>&times;</button>
                <iframe
                  src="https://www.youtube.com/embed/9ico2BLKpWo?autoplay=1"
                  title="ReviewSight Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', borderRadius: '12px' }}
                />
              </div>
            </div>
          )}
      </section>

      {/* ===== Trust Bar ===== */}
      <section className="landing-trust-bar" aria-label={t('landing.trustBar.ariaLabel')}>
        <div className="trust-inner">
          <p className="trust-label">{t('landing.trustBar.label')}</p>
          <div className="trust-logos" aria-hidden="true">
            <div className="trust-logo">Publicis</div>
            <div className="trust-logo">Doctolib</div>
            <div className="trust-logo">BNP Paribas</div>
            <div className="trust-logo">Thales</div>
            <div className="trust-logo">OVHcloud</div>
            <div className="trust-logo">Dassault</div>
          </div>
        </div>
      </section>

      {/* ===== Stats ===== */}
      <section className="landing-stats" aria-label={t('landing.stats.ariaLabel')}>
        <div className="stats-inner">
          {stats.map((stat, i) => (
            <div key={i} className="landing-stat">
              <div className="landing-stat-value">{stat.value}</div>
              <div className="landing-stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Features ===== */}
      <section id="features" className="landing-features" aria-labelledby="features-title">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">{t('landing.features.sectionTag')}</span>
            <h2 id="features-title">{t('landing.features.title')}</h2>
            <p className="section-desc">
              {t('landing.features.description')}
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, i) => (
              <article key={i} className="feature-card">
                {feature.badge && (
                  <span className={`feature-badge badge-${feature.badge.toLowerCase()}`}>
                    {feature.badge}
                  </span>
                )}
                <div className="feature-icon-wrap" aria-hidden="true">
                  {featureIconsMap[featureIcons[i]]}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How It Works ===== */}
      <section id="how-it-works" className="landing-how" aria-labelledby="how-title">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">{t('landing.howItWorks.sectionTag')}</span>
            <h2 id="how-title">{t('landing.howItWorks.title')}</h2>
            <p className="section-desc">
              {t('landing.howItWorks.description')}
            </p>
          </div>
          <div className="how-steps">
            {howItWorksSteps.map((step, i) => (
              <React.Fragment key={i}>
                <div className="how-step">
                  <div className="step-number" aria-hidden="true">{step.number}</div>
                  <div className="step-icon-wrap" aria-hidden="true">
                    {stepIcons[stepIconKeys[i]]}
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {i < howItWorksSteps.length - 1 && (
                  <div className="how-connector" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Use Cases ===== */}
      <section id="use-cases" className="landing-use-cases" aria-labelledby="use-cases-title">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">{t('landing.useCases.sectionTag')}</span>
            <h2 id="use-cases-title">{t('landing.useCases.title')}</h2>
            <p className="section-desc">
              {t('landing.useCases.description')}
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((uc, i) => (
              <article key={i} className="use-case-card">
                <div className="use-case-icon" aria-hidden="true">
                  {useCaseIconsMap[useCaseIconKeys[i]]}
                </div>
                <h3>{uc.segment}</h3>
                <div className="use-case-pain">
                  <span className="pain-label">{t('landing.useCases.painLabel')}</span>
                  <p>{uc.pain}</p>
                </div>
                <div className="use-case-solution">
                  <span className="solution-label">{t('landing.useCases.solutionLabel')}</span>
                  <p>{uc.solution}</p>
                </div>
                <div className="use-case-metric">{uc.metric}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="pricing" className="landing-pricing" aria-labelledby="pricing-title">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">{t('landing.pricing.sectionTag')}</span>
            <h2 id="pricing-title">{t('landing.pricing.title')}</h2>
            <p className="section-desc">
              {t('landing.pricing.description')}
            </p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan, i) => (
              <div key={i} className={`pricing-card ${planPopular[i] ? 'pricing-popular' : ''}`}>
                {planPopular[i] && (
                  <div className="popular-badge" aria-label={t('landing.pricing.popularAriaLabel')}>
                    {t('landing.pricing.popularBadge')}
                  </div>
                )}
                <div className="pricing-header">
                  <h3>{plan.name}</h3>
                  <p className="pricing-desc">{plan.description}</p>
                  <div className="pricing-price">
                    <span className="price-currency">€</span>
                    <span className="price-amount">{plan.price}</span>
                    <span className="price-period">{t('landing.pricing.period')}</span>
                  </div>
                </div>
                <ul className="pricing-features" aria-label={`Fonctionnalités du plan ${plan.name}`}>
                  {plan.features.map((f, j) => (
                    <li key={j}>
                      <span className="check-icon" aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={planLinks[i]}
                  className={`btn-pricing ${planPopular[i] ? 'btn-pricing-primary' : ''}`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="pricing-note">
            {t('landing.pricing.note')}
          </p>
        </div>
      </section>

      {/* ===== Testimonials ===== */}
      <section id="testimonials" className="landing-testimonials" aria-labelledby="testimonials-title">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">{t('landing.testimonials.sectionTag')}</span>
            <h2 id="testimonials-title">{t('landing.testimonials.title')}</h2>
            <p className="section-desc">
              {t('landing.testimonials.description')}
            </p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, i) => (
              <blockquote key={i} className="testimonial-card">
                <div className="testimonial-stars star" role="img" aria-label={t('landing.testimonials.starsAriaLabel', { count: testimonialRatings[i] })}>
                  {'★'.repeat(testimonialRatings[i])}
                </div>
                <p className="testimonial-text">« {testimonial.text} »</p>
                <footer className="testimonial-author">
                  <div className="testimonial-avatar" aria-hidden="true">{testimonialAvatars[i]}</div>
                  <div>
                    <cite className="testimonial-name">{testimonial.name}</cite>
                    <div className="testimonial-role">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="landing-faq" aria-labelledby="faq-title">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">{t('landing.faq.sectionTag')}</span>
            <h2 id="faq-title">{t('landing.faq.title')}</h2>
            <p className="section-desc">
              {t('landing.faq.description')}
              <Link to={langPrefix + '/contact'} className="faq-contact-link">{t('landing.faq.contactLink')}</Link>
            </p>
          </div>
          <div className="faq-list" role="list">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`faq-item ${activeFaq === i ? 'faq-open' : ''}`}
                onClick={() => toggleFaq(i)}
                role="listitem"
              >
                <button
                  className="faq-question"
                  aria-expanded={activeFaq === i}
                  aria-controls={`faq-answer-${i}`}
                >
                  <span>{faq.q}</span>
                  <span className="faq-toggle" aria-hidden="true">
                    {activeFaq === i ? '−' : '+'}
                  </span>
                </button>
                <div
                  id={`faq-answer-${i}`}
                  className="faq-answer"
                  aria-hidden={activeFaq !== i}
                >
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="landing-final-cta" aria-labelledby="cta-title">
        <div className="section-inner">
          <div className="cta-card">
            <div className="cta-orb cta-orb-1" aria-hidden="true"></div>
            <div className="cta-orb cta-orb-2" aria-hidden="true"></div>
            <div className="cta-badge">{t('landing.cta.badge')}</div>
            <h2 id="cta-title">
              {t('landing.cta.title')}<br />
              {t('landing.cta.titleLine2')}
            </h2>
            <p>
              {t('landing.cta.subtitle')}
            </p>
            <div className="cta-actions">
              <Link to={langPrefix + '/register'} className="btn-cta-primary">
                {t('landing.cta.ctaPrimary')}
              </Link>
              <a href="#pricing" className="btn-cta-outline">
                {t('landing.cta.ctaOutline')}
              </a>
            </div>
            <div className="cta-trust">
              <span>{t('landing.cta.trust1')}</span>
              <span>{t('landing.cta.trust2')}</span>
              <span>{t('landing.cta.trust3')}</span>
              <span>{t('landing.cta.trust4')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="landing-footer" role="contentinfo">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="landing-logo" aria-label="ReviewSight">
              Review<span>Sight</span>
            </div>
            <p>
              {t('landing.footer.tagline')}
            </p>
          </div>

          <div className="footer-links">
            <nav className="footer-col" aria-label={t('landing.footer.product')}>
              <h3>{t('landing.footer.product')}</h3>
              <a href="#features">{t('landing.footer.features')}</a>
              <a href="#pricing">{t('landing.footer.pricing')}</a>
              <Link to={langPrefix + '/changelog'}>{t('landing.footer.changelog')}</Link>
              <Link to={langPrefix + '/roadmap'}>{t('landing.footer.roadmap')}</Link>
              <Link to={langPrefix + '/documentation'}>{t('landing.footer.api')}</Link>
            </nav>
            <nav className="footer-col" aria-label={t('landing.footer.resources')}>
              <h3>{t('landing.footer.resources')}</h3>
              <Link to={langPrefix + '/docs'}>{t('landing.footer.documentation')}</Link>
              <Link to={langPrefix + '/guides'}>{t('landing.footer.guides')}</Link>
              <Link to={langPrefix + '/blog'}>{t('landing.footer.blog')}</Link>
              <Link to={langPrefix + '/status'}>{t('landing.footer.status')}</Link>
                      <Link to={langPrefix + '/faq'}>{t('landing.footer.faq')}</Link>
              <Link to={langPrefix + '/vs/bugherd'}>{t('landing.footer.compare')}</Link>
            </nav>
            <nav className="footer-col" aria-label={t('landing.footer.company')}>
              <h3>{t('landing.footer.company')}</h3>
              <Link to={langPrefix + '/a-propos'}>{t('landing.footer.about')}</Link>
              <Link to={langPrefix + '/contact'}>{t('landing.footer.contact')}</Link>
            </nav>
            <nav className="footer-col" aria-label={t('landing.footer.legal')}>
              <h3>{t('landing.footer.legal')}</h3>
              <Link to={langPrefix + '/cgu'}>{t('landing.footer.cgu')}</Link>
              <Link to={langPrefix + '/confidentialite'}>{t('landing.footer.privacy')}</Link>
              <Link to={langPrefix + '/rgpd'}>{t('landing.footer.rgpd')}</Link>
            </nav>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            {t('landing.footer.copyright')}
          </p>
          <div className="footer-bottom-links">
            <p>{t('landing.footer.madeWith')}</p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Landing;
